'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  Check,
  CheckCircle2,
  Clock3,
  CreditCard,
  LoaderCircle,
  Plus,
  Scissors,
  ShieldCheck,
  Store,
  Tags,
  Trash2,
  UserRound,
} from 'lucide-react';
import {
  BusinessClassificationSelector,
  BusinessClassificationSummary,
} from '@/components/business-category/BusinessClassificationSelector';
import { ProfessionalUpsellDialog } from '@/components/subscription/ProfessionalUpsellDialog';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  DAY_LABELS,
  type ScheduleInput,
} from '@/lib/dashboard/work-schedules';
import { INCLUDED_PROFESSIONALS } from '@/content/pricing';
import { cn } from '@/lib/utils';
import {
  AuthServiceError,
  requestRegistrationOtp,
  verifyRegistrationOtp,
} from '@/lib/auth/auth-service';
import { DEFAULT_OTP_RESEND_COOLDOWN_SECONDS, OTP_LENGTH } from '@/lib/auth/constants';
import { trackEvent } from '@/lib/analytics';
import {
  getBusinessTypeDisplayName,
  isBusinessClassificationComplete,
  type BusinessCategoryOption,
  type BusinessClassificationSelection,
} from '@/lib/business-taxonomy';
import {
  FREE_PLAN_CODE,
  MERCADO_PAGO_PLAN_CODE,
  MONTHLY_BILLING_PRICES,
  calculateRegistrationMonthlyTotal,
  createBillingIdempotencyKey,
  saveMercadoPagoCheckoutContext,
  startMercadoPagoSubscription,
  type MercadoPagoCheckoutBusiness,
  type MercadoPagoSubscription,
  type RegistrationPlanCode,
} from '@/lib/billing';
import { fetchActiveBusinessTaxonomy } from '@/lib/dashboard/business-categories';
import { getSupabaseBrowserClient } from '@/lib/supabase/client';

type RegistrationStepId =
  | 'negocio'
  | 'actividad'
  | 'profesionales'
  | 'servicios'
  | 'horarios'
  | 'suscripcion';
type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

type ProfessionalDraft = {
  id: string;
  nombre: string;
  telefono: string;
};

type ServiceDraft = {
  id: string;
  duracionMinutos: number;
  nombre: string;
  precioCop: string;
};

type RegistrationPayload = {
  verification_token: string;
  clasificaciones: Array<{
    category_id: string;
    is_primary: boolean;
    other_business_type_text?: string;
  }>;
  negocio: {
    nombre: string;
    telefono: string;
  };
  profesionales: Array<{
    horarios: ScheduleInput[];
    nombre: string;
    servicios: Array<{
      duracion_minutos: number;
      nombre: string;
      precio_cop?: number;
    }>;
    telefono: string;
  }>;
  suscripcion: {
    cancelar_al_final: boolean;
    cupos_extra: number;
    plan_codigo: RegistrationPlanCode;
  };
};

const STEPS: Array<{
  description: string;
  id: RegistrationStepId;
  label: string;
  title: string;
  icon: typeof Store;
}> = [
  {
    id: 'negocio',
    icon: Store,
    label: 'Negocio',
    title: 'Datos del negocio',
    description: 'La informacion base para crear el perfil operativo.',
  },
  {
    id: 'actividad',
    icon: Tags,
    label: 'Actividad',
    title: '¿Qué tipo de negocio tienes?',
    description: 'Selecciona la opción que más se parezca. Podrás modificarla después.',
  },
  {
    id: 'profesionales',
    icon: UserRound,
    label: 'Profesionales',
    title: 'Equipo de trabajo',
    description: 'Personas que atenderan citas en la agenda.',
  },
  {
    id: 'servicios',
    icon: Scissors,
    label: 'Servicios',
    title: 'Catalogo de servicios',
    description: 'Servicios que quedaran disponibles para reserva.',
  },
  {
    id: 'horarios',
    icon: Clock3,
    label: 'Horarios',
    title: 'Disponibilidad semanal',
    description: 'Franja de atencion inicial para el negocio.',
  },
  {
    id: 'suscripcion',
    icon: CreditCard,
    label: 'Suscripcion',
    title: 'Plan y confirmacion',
    description: 'Revision final antes de crear el negocio.',
  },
];

const INPUT_CLASS =
  'h-11 w-full min-w-0 max-w-full rounded-button border border-border-strong bg-bg-primary/70 px-4 text-sm text-text-primary outline-none transition-colors placeholder:text-text-secondary/60 focus:border-accent focus:ring-2 focus:ring-accent/20';
const BUSINESS_NAME_MAX_LENGTH = 40;

function createDraftId(prefix: string): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function createDefaultSchedules(): ScheduleInput[] {
  return [1, 2, 3, 4, 5, 6, 7].map((day) => ({
    dia_semana: day,
    hora_inicio: day === 7 ? '' : '09:00',
    hora_fin: day === 7 ? '' : '17:00',
  }));
}

function sanitizePhoneInput(value: string): string {
  return value.replace(/\D/g, '').slice(0, 15);
}

function normalizePhone(value: string): string {
  return sanitizePhoneInput(value);
}

function normalizeBusinessPhone(value: string): string {
  const normalized = normalizePhone(value);

  if (/^3\d{9}$/.test(normalized)) {
    return `57${normalized}`;
  }

  return normalized;
}

function isValidBusinessPhone(value: string): boolean {
  return /^57\d{10}$/.test(normalizeBusinessPhone(value));
}

function isPresentPhone(value: string): boolean {
  return normalizePhone(value).length > 0;
}

function formatPhoneForDisplay(value: string): string {
  const normalized = normalizeBusinessPhone(value);
  return normalized ? `+${normalized}` : '';
}

function normalizeDuration(value: number): number {
  const safeValue = Number.isFinite(value) && value > 0 ? value : 30;
  return Math.max(15, Math.min(360, Math.ceil(safeValue / 15) * 15));
}

function normalizeSchedules(schedules: ScheduleInput[]): ScheduleInput[] {
  return schedules
    .filter((schedule) => schedule.hora_inicio && schedule.hora_fin && schedule.hora_inicio < schedule.hora_fin)
    .map((schedule) => ({
      dia_semana: schedule.dia_semana,
      hora_inicio: schedule.hora_inicio,
      hora_fin: schedule.hora_fin,
    }));
}

function formatCompactCurrency(value: number): string {
  return `$${new Intl.NumberFormat('es-CO').format(value)}`;
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function getRegistrationBusiness(
  payload: unknown
): MercadoPagoCheckoutBusiness | null {
  if (!isRecord(payload)) return null;

  const candidate = isRecord(payload.negocio)
    ? payload.negocio
    : isRecord(payload.business)
      ? payload.business
      : null;
  if (!candidate) return null;

  const id = candidate.id ?? candidate.businessId ?? candidate.business_id;
  if (typeof id !== 'string' || !id.trim()) return null;

  const name = candidate.name ?? candidate.nombre;
  const phone = candidate.phone ?? candidate.telefono;

  return {
    id: id.trim(),
    ...(typeof name === 'string' && name.trim() ? { name: name.trim() } : {}),
    ...(typeof phone === 'string' && phone.trim()
      ? { phone: phone.trim() }
      : {}),
  };
}

function getRegistrationSubscription(
  payload: unknown
): MercadoPagoSubscription | null {
  if (!isRecord(payload)) return null;
  const candidate = payload.subscription ?? payload.suscripcion;
  return isRecord(candidate) ? (candidate as MercadoPagoSubscription) : null;
}

function getScheduleSummary(schedules: ScheduleInput[]): string {
  const validSchedules = normalizeSchedules(schedules);

  if (validSchedules.length === 0) {
    return 'Sin horarios';
  }

  const mondayToSaturday = [1, 2, 3, 4, 5, 6].every((day) =>
    validSchedules.some(
      (schedule) =>
        schedule.dia_semana === day &&
        schedule.hora_inicio === validSchedules[0]?.hora_inicio &&
        schedule.hora_fin === validSchedules[0]?.hora_fin
    )
  );

  if (mondayToSaturday && validSchedules.length === 6) {
    return `Lun - Sab, ${validSchedules[0].hora_inicio} - ${validSchedules[0].hora_fin}`;
  }

  return `${validSchedules.length} ${validSchedules.length === 1 ? 'dia' : 'dias'} configurados`;
}

function getResponseMessage(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;

  if (typeof record.message === 'string' && record.message.trim()) {
    return record.message;
  }

  if (typeof record.details === 'string' && record.details.trim()) {
    return record.details;
  }

  if (typeof record.error === 'string' && record.error.trim()) {
    return record.error;
  }

  if (
    record.error &&
    typeof record.error === 'object' &&
    typeof (record.error as Record<string, unknown>).message === 'string'
  ) {
    return (record.error as Record<string, string>).message;
  }

  return null;
}

function StepShell({
  actions,
  children,
  step,
}: {
  actions: ReactNode;
  children: ReactNode;
  step: (typeof STEPS)[number];
}) {
  const Icon = step.icon;

  return (
    <Card className="flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-hidden p-0 shadow-mockup">
      <CardHeader className="min-w-0 shrink-0 border-b border-border p-4 sm:p-5">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-button border border-accent/25 bg-accent/10 text-accent">
            <Icon size={21} />
          </div>
          <div className="min-w-0">
            <CardTitle className="text-xl sm:text-2xl">{step.title}</CardTitle>
            <p className="mt-1 text-sm leading-6 text-text-secondary">{step.description}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="min-h-0 w-full min-w-0 max-w-full flex-1 overflow-y-auto p-4 sm:p-5">
        {children}
      </CardContent>
      <div className="hidden shrink-0 justify-between gap-3 border-t border-border bg-bg-primary/55 p-4 lg:flex">
        {actions}
      </div>
    </Card>
  );
}

export function RegistroForm() {
  const [currentStep, setCurrentStep] = useState(0);
  const [maxStepIndex, setMaxStepIndex] = useState(0);
  const [businessName, setBusinessName] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [businessCategories, setBusinessCategories] = useState<BusinessCategoryOption[]>([]);
  const [primaryClassification, setPrimaryClassification] =
    useState<BusinessClassificationSelection | null>(null);
  const [additionalClassifications, setAdditionalClassifications] = useState<
    BusinessClassificationSelection[]
  >([]);
  const [additionalClassificationDraft, setAdditionalClassificationDraft] =
    useState<BusinessClassificationSelection | null>(null);
  const [isAddingClassification, setIsAddingClassification] = useState(false);
  const [isEditingClassification, setIsEditingClassification] = useState(true);
  const [isLoadingTaxonomy, setIsLoadingTaxonomy] = useState(true);
  const [taxonomyError, setTaxonomyError] = useState<string | null>(null);
  const [upsellContext, setUpsellContext] = useState<
    'additional-category' | 'professional-limit' | 'service-limit' | null
  >(null);
  const [otp, setOtp] = useState('');
  const [otpChallengeId, setOtpChallengeId] = useState<string | null>(null);
  const [otpVerificationToken, setOtpVerificationToken] = useState<string | null>(null);
  const [verifiedBusinessPhone, setVerifiedBusinessPhone] = useState<string | null>(null);
  const [otpStatus, setOtpStatus] = useState<'idle' | 'sending' | 'sent' | 'verifying' | 'verified'>('idle');
  const [otpMessage, setOtpMessage] = useState<string | null>(null);
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);
  const [professionals, setProfessionals] = useState<ProfessionalDraft[]>([
    { id: 'professional-1', nombre: '', telefono: '' },
  ]);
  const [services, setServices] = useState<ServiceDraft[]>([
    { id: 'service-1', duracionMinutos: 60, nombre: '', precioCop: '' },
  ]);
  const [schedules, setSchedules] = useState<ScheduleInput[]>(() => createDefaultSchedules());
  const [feedback, setFeedback] = useState<string | null>(null);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] =
    useState<RegistrationPlanCode>(FREE_PLAN_CODE);
  const [payerEmail, setPayerEmail] = useState('');

  const extraSlots = Math.max(0, professionals.length - INCLUDED_PROFESSIONALS);
  const monthlyTotal = calculateRegistrationMonthlyTotal(
    selectedPlan,
    professionals.length
  );
  const validSchedules = useMemo(() => normalizeSchedules(schedules), [schedules]);
  const scheduleSummary = useMemo(() => getScheduleSummary(schedules), [schedules]);
  const normalizedBusinessPhone = normalizeBusinessPhone(businessPhone);

  useEffect(() => {
    const requestedPlan = new URLSearchParams(window.location.search)
      .get('plan')
      ?.trim()
      .toUpperCase();

    if (requestedPlan === MERCADO_PAGO_PLAN_CODE) {
      setSelectedPlan(MERCADO_PAGO_PLAN_CODE);
    }
  }, []);

  useEffect(() => {
    const requestedPlan = new URLSearchParams(window.location.search)
      .get('plan')
      ?.trim()
      .toUpperCase();
    if (requestedPlan === MERCADO_PAGO_PLAN_CODE) {
      setSelectedPlan(MERCADO_PAGO_PLAN_CODE);
    }
  }, []);

  useEffect(() => {
    if (otpSecondsLeft <= 0) return;
    const timer = window.setInterval(() => setOtpSecondsLeft((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [otpSecondsLeft]);

  useEffect(() => {
    const client = getSupabaseBrowserClient();
    let ignore = false;

    if (!client) {
      setTaxonomyError('No se pudo conectar con el catálogo de actividades.');
      setIsLoadingTaxonomy(false);
      return;
    }
    const supabaseClient = client;

    async function loadTaxonomy() {
      setIsLoadingTaxonomy(true);
      setTaxonomyError(null);

      try {
        const categories = await fetchActiveBusinessTaxonomy(supabaseClient);
        if (ignore) return;
        setBusinessCategories(categories);
        if (categories.length === 0) {
          setTaxonomyError('No hay actividades disponibles en este momento.');
        }
      } catch {
        if (!ignore) {
          setTaxonomyError('No pudimos cargar las actividades. Intenta de nuevo.');
        }
      } finally {
        if (!ignore) setIsLoadingTaxonomy(false);
      }
    }

    void loadTaxonomy();
    return () => {
      ignore = true;
    };
  }, []);

  const validateStep = (stepIndex: number): string | null => {
    const stepId = STEPS[stepIndex]?.id;

    if (stepId === 'negocio') {
      if (businessName.trim().length < 2) {
        return 'Ingresa el nombre del negocio.';
      }

      if (businessName.trim().length > BUSINESS_NAME_MAX_LENGTH) {
        return `El nombre del negocio no puede tener mas de ${BUSINESS_NAME_MAX_LENGTH} caracteres.`;
      }

      if (!isValidBusinessPhone(businessPhone)) {
        return 'Ingresa un telefono de negocio valido. Usa el indicativo 57, por ejemplo 573001234567.';
      }

      if (!otpVerificationToken || verifiedBusinessPhone !== normalizedBusinessPhone) {
        return 'Verifica por WhatsApp que eres propietario del numero del negocio.';
      }

    }

    if (stepId === 'actividad') {
      if (isLoadingTaxonomy) {
        return 'Espera un momento mientras cargamos las actividades.';
      }

      if (taxonomyError) {
        return taxonomyError;
      }

      if (!isBusinessClassificationComplete(primaryClassification)) {
        return primaryClassification?.requiresCustomText
          ? 'Describe brevemente la actividad de tu negocio.'
          : 'Selecciona la categoría y el tipo de negocio.';
      }

      if (isAddingClassification) {
        return 'Guarda o cancela la actividad adicional antes de continuar.';
      }
    }

    if (stepId === 'profesionales') {
      if (professionals.length === 0) {
        return 'Agrega al menos un profesional.';
      }

      const invalidProfessional = professionals.find(
        (professional) => professional.nombre.trim().length < 2 || !isPresentPhone(professional.telefono)
      );

      if (invalidProfessional) {
        return 'Completa nombre y telefono de cada profesional.';
      }
    }

    if (stepId === 'servicios') {
      if (services.length === 0) {
        return 'Agrega al menos un servicio.';
      }

      const invalidService = services.find((service) => {
        const hasPrice = service.precioCop.trim().length > 0;
        const price = Number(service.precioCop);
        return (
          service.nombre.trim().length < 2 ||
          (hasPrice && !Number.isFinite(price)) ||
          price < 0 ||
          !Number.isFinite(service.duracionMinutos) ||
          service.duracionMinutos < 15 ||
          service.duracionMinutos > 360
        );
      });

      if (invalidService) {
        return 'Completa nombre y duracion de cada servicio. Si agregas precio, debe ser valido.';
      }
    }

    if (stepId === 'horarios') {
      const hasPartialSchedule = schedules.some(
        (schedule) =>
          (schedule.hora_inicio && !schedule.hora_fin) ||
          (!schedule.hora_inicio && schedule.hora_fin) ||
          (schedule.hora_inicio && schedule.hora_fin && schedule.hora_inicio >= schedule.hora_fin)
      );

      if (hasPartialSchedule) {
        return 'Revisa que cada dia abierto tenga hora de inicio menor a la hora final.';
      }

      if (validSchedules.length === 0) {
        return 'Configura al menos un dia de atencion.';
      }
    }

    if (
      stepId === 'suscripcion' &&
      selectedPlan === MERCADO_PAGO_PLAN_CODE &&
      !isValidEmail(payerEmail)
    ) {
      return 'Ingresa un correo válido para continuar con el pago.';
    }

    return null;
  };

  const validateUntil = (targetStep: number): string | null => {
    for (let index = 0; index < targetStep; index += 1) {
      const message = validateStep(index);

      if (message) {
        setCurrentStep(index);
        return message;
      }
    }

    return null;
  };

  const goToStep = (stepIndex: number) => {
    if (stepIndex > currentStep) {
      const message = validateUntil(stepIndex);

      if (message) {
        setFeedback(message);
        return;
      }
    }

    if (stepIndex <= maxStepIndex) {
      setCurrentStep(stepIndex);
      setFeedback(null);
      return;
    }

    const message = validateUntil(stepIndex);

    if (message) {
      setFeedback(message);
      return;
    }

    setMaxStepIndex((current) => Math.max(current, stepIndex));
    setCurrentStep(stepIndex);
    setFeedback(null);
  };

  const goNext = () => {
    const message = validateStep(currentStep);

    if (message) {
      setFeedback(message);
      return;
    }

    const nextStep = Math.min(STEPS.length - 1, currentStep + 1);
    setMaxStepIndex((index) => Math.max(index, nextStep));
    setCurrentStep(nextStep);
    setFeedback(null);
  };

  const goBack = () => {
    setCurrentStep((step) => Math.max(0, step - 1));
    setFeedback(null);
  };

  const handleBusinessNameChange = (value: string) => {
    setBusinessName(value.slice(0, BUSINESS_NAME_MAX_LENGTH));
  };

  const handleBusinessPhoneChange = (value: string) => {
    const nextPhone = sanitizePhoneInput(value);
    setBusinessPhone(nextPhone);
    if (normalizeBusinessPhone(nextPhone) !== verifiedBusinessPhone) {
      setOtp('');
      setOtpChallengeId(null);
      setOtpVerificationToken(null);
      setOtpStatus('idle');
      setOtpMessage(null);
    }
  };

  const handleRequestRegistrationOtp = async () => {
    if (!isValidBusinessPhone(businessPhone)) {
      setFeedback('Ingresa un telefono de negocio valido antes de solicitar el codigo.');
      return;
    }
    setOtpStatus('sending');
    setFeedback(null);
    setOtpMessage(null);
    try {
      const result = await requestRegistrationOtp(normalizedBusinessPhone);
      if (!result.challengeId) throw new Error('No fue posible iniciar la verificacion.');
      setOtpChallengeId(result.challengeId);
      setOtp('');
      setOtpStatus('sent');
      setOtpMessage(result.message);
      setOtpSecondsLeft(DEFAULT_OTP_RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      setOtpStatus('idle');
      setFeedback(error instanceof AuthServiceError || error instanceof Error
        ? error.message
        : 'No fue posible enviar el codigo por WhatsApp.');
    }
  };

  const handleVerifyRegistrationOtp = async () => {
    if (!otpChallengeId || otp.length !== OTP_LENGTH) {
      setFeedback(`Ingresa el codigo de ${OTP_LENGTH} digitos enviado por WhatsApp.`);
      return;
    }
    setOtpStatus('verifying');
    setFeedback(null);
    try {
      const token = await verifyRegistrationOtp({
        challengeId: otpChallengeId,
        phone: normalizedBusinessPhone,
        otp,
      });
      setOtpVerificationToken(token);
      setVerifiedBusinessPhone(normalizedBusinessPhone);
      setOtpStatus('verified');
      setOtpMessage('Numero verificado correctamente.');
    } catch (error) {
      setOtpStatus('sent');
      setFeedback(error instanceof AuthServiceError || error instanceof Error
        ? error.message
        : 'No fue posible validar el codigo.');
    }
  };

  const updateProfessional = (id: string, field: keyof Omit<ProfessionalDraft, 'id'>, value: string) => {
    setProfessionals((current) =>
      current.map((professional) =>
        professional.id === id
          ? {
              ...professional,
              [field]: field === 'telefono' ? sanitizePhoneInput(value) : value,
            }
          : professional
      )
    );
  };

  const addProfessional = () => {
    if (
      selectedPlan === FREE_PLAN_CODE &&
      professionals.length >= INCLUDED_PROFESSIONALS
    ) {
      setUpsellContext('professional-limit');
      return;
    }

    setProfessionals((current) => [
      ...current,
      { id: createDraftId('professional'), nombre: '', telefono: '' },
    ]);
  };

  const removeProfessional = (id: string) => {
    setProfessionals((current) =>
      current.length === 1 ? current : current.filter((professional) => professional.id !== id)
    );
  };

  const updateService = (id: string, field: keyof Omit<ServiceDraft, 'id'>, value: string | number) => {
    setServices((current) =>
      current.map((service) =>
        service.id === id
          ? {
              ...service,
              [field]:
                field === 'precioCop'
                  ? String(value).replace(/\D/g, '').slice(0, 9)
                  : field === 'duracionMinutos'
                    ? Number(value)
                    : String(value),
            }
          : service
      )
    );
  };

  const addService = () => {
    if (selectedPlan === FREE_PLAN_CODE && services.length >= 3) {
      trackEvent('service_limit_reached', {
        service_count: services.length,
        source: 'registration',
      });
      setUpsellContext('service-limit');
      return;
    }

    setServices((current) => [
      ...current,
      { id: createDraftId('service'), duracionMinutos: 60, nombre: '', precioCop: '' },
    ]);
  };

  const selectProfessionalPlan = () => {
    const upgradeContext = upsellContext;
    setSelectedPlan(MERCADO_PAGO_PLAN_CODE);
    setUpsellContext(null);
    setFeedback(null);

    if (upgradeContext === 'professional-limit') {
      setProfessionals((current) => [
        ...current,
        { id: createDraftId('professional'), nombre: '', telefono: '' },
      ]);
    }

    if (upgradeContext === 'service-limit') {
      setServices((current) => [
        ...current,
        {
          id: createDraftId('service'),
          duracionMinutos: 60,
          nombre: '',
          precioCop: '',
        },
      ]);
    }

    if (upgradeContext === 'additional-category') {
      setAdditionalClassificationDraft(null);
      setIsAddingClassification(true);
    }
  };

  const selectFreePlan = () => {
    setSelectedPlan(FREE_PLAN_CODE);
    const hasHiddenExtras =
      professionals.length > INCLUDED_PROFESSIONALS ||
      services.length > 3 ||
      additionalClassifications.length > 0;
    setFeedback(
      hasHiddenExtras
        ? 'Puedes finalizar con Gratis. Tus datos adicionales quedarán guardados y se activarán cuando elijas Profesional.'
        : null
    );
  };

  const openAdditionalClassification = () => {
    if (additionalClassifications.length >= 9) {
      setFeedback('El Plan Profesional permite hasta 10 categorías de actividad.');
      return;
    }
    if (selectedPlan === FREE_PLAN_CODE) {
      setUpsellContext('additional-category');
      return;
    }
    setAdditionalClassificationDraft(null);
    setIsAddingClassification(true);
    setFeedback(null);
  };

  const saveAdditionalClassification = () => {
    if (!isBusinessClassificationComplete(additionalClassificationDraft)) {
      setFeedback(
        additionalClassificationDraft?.requiresCustomText
          ? 'Describe brevemente la actividad adicional.'
          : 'Selecciona la categoría y el tipo de negocio adicional.'
      );
      return;
    }

    const selection = additionalClassificationDraft!;
    const usedTypeIds = new Set([
      primaryClassification?.typeId,
      ...additionalClassifications.map((item) => item.typeId),
    ]);
    if (usedTypeIds.has(selection.typeId)) {
      setFeedback('Esta actividad ya está agregada.');
      return;
    }

    setAdditionalClassifications((current) => [...current, selection]);
    setAdditionalClassificationDraft(null);
    setIsAddingClassification(false);
    setFeedback(null);
  };

  const removeService = (id: string) => {
    setServices((current) => (current.length === 1 ? current : current.filter((service) => service.id !== id)));
  };

  const updateScheduleDay = (dayIndex: number, field: 'hora_inicio' | 'hora_fin', value: string) => {
    setSchedules((current) =>
      current.map((schedule, index) => (index === dayIndex ? { ...schedule, [field]: value } : schedule))
    );
  };

  const toggleScheduleDay = (dayIndex: number) => {
    setSchedules((current) =>
      current.map((schedule, index) => {
        if (index !== dayIndex) {
          return schedule;
        }

        const isOpen = Boolean(schedule.hora_inicio && schedule.hora_fin);

        return isOpen
          ? { ...schedule, hora_inicio: '', hora_fin: '' }
          : { ...schedule, hora_inicio: '09:00', hora_fin: '17:00' };
      })
    );
  };

  const buildPayload = (): RegistrationPayload => ({
    verification_token: otpVerificationToken!,
    clasificaciones: [
      {
        category_id: primaryClassification!.typeId,
        is_primary: true,
        ...(primaryClassification!.otherBusinessTypeText.trim()
          ? {
              other_business_type_text:
                primaryClassification!.otherBusinessTypeText.trim(),
            }
          : {}),
      },
      ...additionalClassifications.map((classification) => ({
        category_id: classification.typeId,
        is_primary: false,
        ...(classification.otherBusinessTypeText.trim()
          ? {
              other_business_type_text:
                classification.otherBusinessTypeText.trim(),
            }
          : {}),
      })),
    ],
    negocio: {
      nombre: businessName.trim(),
      telefono: normalizedBusinessPhone,
    },
    profesionales: professionals.map((professional) => ({
      nombre: professional.nombre.trim(),
      telefono: normalizePhone(professional.telefono),
      servicios: services.map((service) => ({
        nombre: service.nombre.trim(),
        ...(service.precioCop.trim() ? { precio_cop: Number(service.precioCop) } : {}),
        duracion_minutos: normalizeDuration(service.duracionMinutos),
      })),
      horarios: validSchedules,
    })),
    suscripcion: {
      plan_codigo: selectedPlan,
      cupos_extra: extraSlots,
      cancelar_al_final: false,
    },
  });

  const handleSubmit = async () => {
    const message = validateUntil(STEPS.length);

    if (message) {
      setFeedback(message);
      return;
    }

    setSubmitStatus('submitting');
    setSubmitMessage(null);
    setFeedback(null);

    try {
      const response = await fetch('/api/registro-negocio', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(buildPayload()),
      });

      const responsePayload = (await response.json().catch(() => null)) as unknown;

      if (!response.ok) {
        throw new Error(getResponseMessage(responsePayload) ?? 'No fue posible crear el negocio.');
      }

      if (selectedPlan === MERCADO_PAGO_PLAN_CODE) {
        const business = getRegistrationBusiness(responsePayload);
        const registrationSubscription =
          getRegistrationSubscription(responsePayload);

        if (!business) {
          throw new Error(
            'El negocio fue creado, pero no pudimos preparar el pago. Ingresa a tu cuenta para continuar.'
          );
        }

        const idempotencyKey = createBillingIdempotencyKey('registration');
        const checkoutContext = {
          business,
          createdAt: new Date().toISOString(),
          idempotencyKey,
          payerEmail: payerEmail.trim(),
          professionalCount: professionals.length,
          subscription: registrationSubscription,
          token: otpVerificationToken!,
        };
        saveMercadoPagoCheckoutContext(checkoutContext);

        try {
          const checkout = await startMercadoPagoSubscription({
            authToken: otpVerificationToken!,
            businessId: business.id,
            idempotencyKey,
            payerEmail: payerEmail.trim(),
            professionalCount: professionals.length,
          });

          saveMercadoPagoCheckoutContext({
            ...checkoutContext,
            subscription: checkout.subscription ?? registrationSubscription,
          });

          if (!checkout.checkoutUrl) {
            throw new Error('Mercado Pago no devolvió un enlace de pago.');
          }

          window.location.assign(checkout.checkoutUrl);
          return;
        } catch {
          window.location.assign(
            '/suscripcion/confirmacion?status=failed&source=start'
          );
          return;
        }
      }

      setSubmitStatus('success');
      const hasHiddenExtras =
        selectedPlan === FREE_PLAN_CODE &&
        (professionals.length > INCLUDED_PROFESSIONALS ||
          services.length > 3 ||
          additionalClassifications.length > 0);
      setSubmitMessage(
        hasHiddenExtras
          ? 'Negocio creado con el Plan Gratis. Tus recursos adicionales quedaron guardados y ocultos hasta que actives Profesional.'
          : getResponseMessage(responsePayload) ?? 'Negocio creado correctamente.'
      );
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage(error instanceof Error ? error.message : 'No fue posible crear el negocio.');
    }
  };

  const currentStepData = STEPS[currentStep];
  const CurrentStepIcon = currentStepData.icon;
  const isFinalStep = currentStep === STEPS.length - 1;
  const isSubmitting = submitStatus === 'submitting';
  const isSuccess = submitStatus === 'success';

  const stepActions = (
    <>
      <Button type="button" variant="ghost" onClick={goBack} disabled={currentStep === 0 || isSubmitting}>
        <ArrowLeft size={18} />
        Anterior
      </Button>
      {isSuccess ? (
        <Button asChild>
          <Link href="/login/">
            Ir al login
            <ArrowRight size={18} />
          </Link>
        </Button>
      ) : (
        <Button
          type="button"
          onClick={isFinalStep ? handleSubmit : goNext}
          disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {isSubmitting ? <LoaderCircle size={18} className="animate-spin" /> : null}
          {isFinalStep
            ? selectedPlan === MERCADO_PAGO_PLAN_CODE
              ? 'Crear y continuar al pago'
              : 'Crear negocio'
            : 'Siguiente'}
          {!isSubmitting ? <ArrowRight size={18} /> : null}
        </Button>
      )}
    </>
  );

  return (
    <main className="oyb-registration-app fixed inset-0 z-[70] flex w-full min-w-0 max-w-full flex-col overflow-x-hidden bg-bg-primary text-text-primary">
      <header className="w-full min-w-0 max-w-full shrink-0 overflow-x-hidden border-b border-border bg-bg-secondary/95 shadow-[0_12px_32px_rgb(34_31_28/0.08)] backdrop-blur-xl">
        <div className="mx-auto flex w-full min-w-0 max-w-7xl flex-col gap-3 px-4 py-3 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-center justify-between gap-3 lg:w-[260px]">
            <Link href="/" className="min-w-0">
              <span className="block truncate font-heading text-xl font-bold text-accent">OficioYa</span>
              <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-text-secondary">
                Registro
              </span>
            </Link>
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-button border border-border-strong bg-bg-primary text-accent lg:hidden">
              <CurrentStepIcon size={20} />
            </div>
          </div>

          <nav className="w-full min-w-0 max-w-full overflow-hidden lg:w-auto" aria-label="Pasos de registro">
            <div className="flex w-full min-w-0 gap-1.5 lg:grid lg:min-w-[760px] lg:grid-cols-6 lg:gap-2">
              {STEPS.map((step, index) => {
                const Icon = step.icon;
                const isActive = currentStep === index;
                const isDone = index < currentStep || (index < maxStepIndex && !validateStep(index));
                const isEnabled = index <= maxStepIndex;

                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => goToStep(index)}
                    disabled={!isEnabled || isSubmitting}
                    aria-current={isActive ? 'step' : undefined}
                    aria-label={step.label}
                    className={cn(
                      'flex min-h-11 min-w-0 items-center justify-center gap-1.5 rounded-button border px-1 text-xs font-heading font-semibold transition-colors lg:w-auto lg:gap-2 lg:px-2',
                      isActive ? 'flex-1' : 'w-10 shrink-0 lg:w-auto',
                      isActive
                        ? 'border-accent bg-accent text-bg-primary'
                        : isDone
                          ? 'border-success/35 bg-success/10 text-success'
                          : 'border-border-strong bg-bg-primary/75 text-text-secondary',
                      (!isEnabled || isSubmitting) && 'cursor-not-allowed opacity-55'
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px]',
                        isActive
                          ? 'border-bg-primary/40'
                          : isDone
                            ? 'border-success/30 bg-success/10'
                            : 'border-border'
                      )}
                    >
                      <span className="lg:hidden">{index + 1}</span>
                      {isDone ? (
                        <Check size={13} className="hidden lg:block" aria-hidden="true" />
                      ) : (
                        <span className="hidden lg:inline">{index + 1}</span>
                      )}
                    </span>
                    <Icon size={14} className="hidden lg:block" />
                    <span className={cn('min-w-0 truncate', !isActive && 'hidden lg:inline')}>
                      {step.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </nav>
        </div>
      </header>

      <div className="min-h-0 w-full min-w-0 max-w-full flex-1 overflow-y-auto">
        <div className="mx-auto grid min-h-full w-full min-w-0 max-w-7xl grid-cols-[minmax(0,1fr)] gap-5 px-4 py-5 pb-[calc(6.75rem+env(safe-area-inset-bottom))] sm:px-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:pb-5">
          <section className="flex min-h-0 w-full min-w-0 max-w-full flex-col gap-4">
            <div className="shrink-0 hidden lg:block">
              <h1 className="font-heading text-2xl font-semibold text-text-primary sm:text-3xl">
                Registra tu negocio
              </h1>
              <p className="mt-1 text-sm leading-6 text-text-secondary">
                Configura actividad, equipo, servicios, horarios y plan en pocos pasos.
              </p>
            </div>
            <StepShell step={currentStepData} actions={stepActions}>
              {feedback ? (
                <div
                  className="mb-4 flex min-w-0 items-start gap-3 rounded-lg border border-error/35 bg-error/10 px-4 py-3 text-sm text-error"
                  role="alert"
                >
                  <AlertCircle size={18} className="mt-0.5 shrink-0" />
                  <p className="min-w-0 break-words">{feedback}</p>
                </div>
              ) : null}

              {currentStepData.id === 'negocio' ? (
                <div className="grid min-w-0 gap-4 sm:grid-cols-2">
                  <label className="min-w-0 space-y-2">
                    <span className="text-sm font-semibold text-text-primary">Nombre del negocio</span>
                    <input
                      type="text"
                      value={businessName}
                      onChange={(event) => handleBusinessNameChange(event.target.value)}
                      placeholder="Nombre de tu negocio"
                      maxLength={BUSINESS_NAME_MAX_LENGTH}
                      className={INPUT_CLASS}
                      autoComplete="organization"
                    />
                    <span className="block text-xs text-text-secondary">
                      {businessName.length}/{BUSINESS_NAME_MAX_LENGTH} caracteres
                    </span>
                  </label>
                  <label className="min-w-0 space-y-2">
                    <span className="text-sm font-semibold text-text-primary">Teléfono del Negocio</span>
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={businessPhone}
                      onChange={(event) => handleBusinessPhoneChange(event.target.value)}
                      placeholder="573018160243"
                      className={INPUT_CLASS}
                      autoComplete="tel"
                    />
                    <span className="hidden text-xs text-text-secondary lg:block">
                      Usa indicativo de país. También aceptamos 3001234567 y lo convertimos a 573001234567.
                    </span>
                  </label>

                  <div className="min-w-0 space-y-3 rounded-lg border border-border bg-bg-primary/45 p-4 sm:col-span-2">
                    <div className="flex items-start gap-3">
                      <ShieldCheck size={20} className={cn('mt-0.5 shrink-0', otpStatus === 'verified' ? 'text-success' : 'text-accent')} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-text-primary">Comprueba la propiedad del numero</p>
                        <p className="mt-1 text-xs leading-5 text-text-secondary">
                          Enviaremos un codigo de {OTP_LENGTH} digitos por WhatsApp al telefono del negocio.
                        </p>
                      </div>
                    </div>

                    {otpStatus === 'verified' ? (
                      <div className="flex items-center gap-2 rounded-button border border-success/30 bg-success/10 px-3 py-2 text-sm font-semibold text-success">
                        <CheckCircle2 size={18} />
                        {otpMessage}
                      </div>
                    ) : otpChallengeId ? (
                      <div className="grid min-w-0 gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
                        <input
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          value={otp}
                          onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH))}
                          placeholder="123456"
                          aria-label="Codigo de verificacion de WhatsApp"
                          className={INPUT_CLASS}
                        />
                        <Button
                          type="button"
                          onClick={handleVerifyRegistrationOtp}
                          disabled={otpStatus === 'sending' || otpStatus === 'verifying' || otp.length !== OTP_LENGTH}
                        >
                          {otpStatus === 'verifying' ? <LoaderCircle size={18} className="animate-spin" /> : null}
                          Verificar
                        </Button>
                      </div>
                    ) : null}

                    {otpMessage && otpStatus !== 'verified' ? (
                      <p className="text-xs text-text-secondary">{otpMessage}</p>
                    ) : null}

                    {otpStatus !== 'verified' ? (
                      <Button
                        type="button"
                        variant={otpChallengeId ? 'ghost' : 'secondary'}
                        size="sm"
                        onClick={handleRequestRegistrationOtp}
                        disabled={otpStatus === 'sending' || otpStatus === 'verifying' || otpSecondsLeft > 0}
                      >
                        {otpStatus === 'sending' ? <LoaderCircle size={16} className="animate-spin" /> : null}
                        {otpChallengeId
                          ? otpSecondsLeft > 0
                            ? `Reenviar en ${otpSecondsLeft}s`
                            : 'Reenviar codigo'
                          : 'Enviar codigo por WhatsApp'}
                      </Button>
                    ) : null}
                  </div>

                </div>
              ) : null}

              {currentStepData.id === 'actividad' ? (
                <div className="min-w-0 space-y-5">
                  {isLoadingTaxonomy ? (
                    <div
                      className="flex min-h-40 items-center justify-center rounded-lg border border-border bg-bg-primary/40 text-sm text-text-secondary"
                      role="status"
                    >
                      <LoaderCircle size={18} className="mr-2 animate-spin text-accent" />
                      Cargando actividades...
                    </div>
                  ) : taxonomyError ? (
                    <div
                      className="flex items-start gap-3 rounded-lg border border-error/35 bg-error/10 px-4 py-3 text-sm text-error"
                      role="alert"
                    >
                      <AlertCircle size={18} className="mt-0.5 shrink-0" />
                      <p>{taxonomyError}</p>
                    </div>
                  ) : isBusinessClassificationComplete(primaryClassification) &&
                    !isEditingClassification ? (
                    <BusinessClassificationSummary
                      selection={primaryClassification!}
                      onChange={() => setIsEditingClassification(true)}
                    />
                  ) : (
                    <div className="space-y-4">
                      <BusinessClassificationSelector
                        categories={businessCategories}
                        excludedTypeIds={additionalClassifications.map((item) => item.typeId)}
                        value={primaryClassification}
                        onChange={(selection) => {
                          setPrimaryClassification(selection);
                          if (!selection.requiresCustomText) {
                            setIsEditingClassification(false);
                          }
                          setFeedback(null);
                        }}
                        errorMessage={
                          feedback && !isBusinessClassificationComplete(primaryClassification)
                            ? feedback
                            : null
                        }
                        analyticsSource="registration"
                      />
                      {primaryClassification?.requiresCustomText &&
                      isBusinessClassificationComplete(primaryClassification) ? (
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => setIsEditingClassification(false)}
                          >
                            Guardar actividad
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  )}

                  {isBusinessClassificationComplete(primaryClassification) &&
                  !isEditingClassification ? (
                    <>
                      {additionalClassifications.map((classification, index) => (
                        <div key={classification.typeId} className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                          <BusinessClassificationSummary
                            label={`Actividad adicional ${index + 1}`}
                            selection={classification}
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setAdditionalClassifications((current) =>
                                current.filter((item) => item.typeId !== classification.typeId)
                              );
                              setFeedback(null);
                            }}
                            aria-label={`Quitar ${getBusinessTypeDisplayName(classification)}`}
                          >
                            <Trash2 size={17} />
                            Quitar
                          </Button>
                        </div>
                      ))}

                      {isAddingClassification ? (
                        <section className="space-y-4 rounded-lg border border-accent/30 bg-accent/5 p-4">
                          <div>
                            <p className="font-heading font-semibold text-text-primary">Nueva actividad</p>
                            <p className="mt-1 text-sm text-text-secondary">
                              Puedes agregar hasta 10 categorías de actividad con el Plan Profesional.
                            </p>
                          </div>
                          <BusinessClassificationSelector
                            analyticsSource="registration"
                            categories={businessCategories}
                            excludedTypeIds={[
                              primaryClassification!.typeId,
                              ...additionalClassifications.map((item) => item.typeId),
                            ]}
                            value={additionalClassificationDraft}
                            onChange={(selection) => {
                              setAdditionalClassificationDraft(selection);
                              setFeedback(null);
                            }}
                          />
                          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() => {
                                setAdditionalClassificationDraft(null);
                                setIsAddingClassification(false);
                                setFeedback(null);
                              }}
                            >
                              Cancelar
                            </Button>
                            <Button type="button" onClick={saveAdditionalClassification}>
                              Guardar actividad
                            </Button>
                          </div>
                        </section>
                      ) : (
                        <section className="rounded-lg border border-border bg-bg-primary/40 p-4">
                          <p className="text-sm font-semibold text-text-primary">
                            ¿Tu negocio ofrece otro tipo de actividad?
                          </p>
                          <p className="mt-1 text-sm leading-6 text-text-secondary">
                            {selectedPlan === FREE_PLAN_CODE
                              ? 'La segunda actividad requiere el Plan Profesional. Puedes seleccionarlo sin salir del registro.'
                              : `${additionalClassifications.length + 1} de 10 categorías agregadas.`}
                          </p>
                          {additionalClassifications.length < 9 ? (
                            <button
                              type="button"
                              onClick={() => {
                                const classificationCount = additionalClassifications.length + 1;
                                trackEvent('additional_business_type_clicked', {
                                  classification_count: classificationCount,
                                  source: 'registration',
                                });
                                if (selectedPlan === FREE_PLAN_CODE) {
                                  trackEvent('category_limit_reached', {
                                    classification_count: classificationCount,
                                    source: 'registration',
                                  });
                                }
                                openAdditionalClassification();
                              }}
                              className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-button px-1 text-sm font-heading font-semibold text-accent transition-colors hover:text-accent-hover"
                            >
                              <Plus size={17} />
                              Agregar otra actividad
                            </button>
                          ) : (
                            <p className="mt-3 text-sm font-semibold text-success">Alcanzaste el máximo de 10 categorías de actividad.</p>
                          )}
                        </section>
                      )}
                    </>
                  ) : null}
                </div>
              ) : null}

              {currentStepData.id === 'profesionales' ? (
                <div className="min-w-0 space-y-4">
                  <div className="min-w-0 space-y-3">
                    {professionals.map((professional, index) => (
                      <article
                        key={professional.id}
                        className="grid min-w-0 gap-3 rounded-lg border border-border bg-bg-primary/45 p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end"
                      >
                        <label className="min-w-0 space-y-2">
                          <span className="flex flex-wrap items-center justify-between gap-2 text-sm font-semibold text-text-primary">
                            <span>Nombre {professionals.length > 1 ? index + 1 : ''}</span>
                            {index > 0 && selectedPlan === MERCADO_PAGO_PLAN_CODE ? (
                              <span className="rounded-full bg-accent/10 px-2 py-0.5 text-xs text-accent">
                                +{formatCompactCurrency(MONTHLY_BILLING_PRICES.extraProfessionalPriceCop)}/mes
                              </span>
                            ) : null}
                          </span>
                          <input
                            type="text"
                            value={professional.nombre}
                            onChange={(event) => updateProfessional(professional.id, 'nombre', event.target.value)}
                            placeholder="Laura Gómez"
                            className={INPUT_CLASS}
                            autoComplete="name"
                          />
                        </label>
                        <label className="min-w-0 space-y-2">
                          <span className="text-sm font-semibold text-text-primary">Telefono</span>
                          <input
                            type="tel"
                            inputMode="numeric"
                            value={professional.telefono}
                            onChange={(event) => updateProfessional(professional.id, 'telefono', event.target.value)}
                            placeholder="573208351887"
                            className={INPUT_CLASS}
                            autoComplete="tel"
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => removeProfessional(professional.id)}
                          disabled={professionals.length === 1}
                          className="flex h-11 w-full items-center justify-center rounded-button border border-border-strong text-text-secondary transition-colors hover:border-error hover:text-error disabled:cursor-not-allowed disabled:opacity-45 sm:w-11"
                          aria-label="Eliminar profesional"
                          title="Eliminar"
                        >
                          <Trash2 size={17} />
                        </button>
                      </article>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={addProfessional}
                    className="flex min-h-11 w-full items-center justify-center gap-2 rounded-button border border-dashed border-accent/65 bg-bg-primary/35 px-4 py-3 font-heading text-sm font-semibold text-accent transition-colors hover:bg-accent/10"
                  >
                    <Plus size={18} />
                    {selectedPlan === MERCADO_PAGO_PLAN_CODE
                      ? `Agregar profesional · +${formatCompactCurrency(MONTHLY_BILLING_PRICES.extraProfessionalPriceCop)}/mes`
                      : 'Agregar profesional'}
                  </button>
                </div>
              ) : null}

              {currentStepData.id === 'servicios' ? (
                <div className="min-w-0 max-w-full space-y-4">
                  <div className="min-w-0 max-w-full overflow-hidden rounded-lg border border-border">
                    <div className="hidden min-w-0 grid-cols-[minmax(0,1fr)_140px_130px_44px] gap-3 border-b border-border bg-bg-primary/60 px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-text-secondary md:grid">
                      <span>Servicio</span>
                      <span>Precio (opcional)</span>
                      <span>Duración (En Minutos)</span>
                      <span />
                    </div>
                    <div className="divide-y divide-border">
                      {services.map((service) => (
                        <article
                          key={service.id}
                          className="grid min-w-0 gap-3 bg-bg-primary/35 p-3 md:grid-cols-[minmax(0,1fr)_140px_130px_44px] md:items-end"
                        >
                          <label className="min-w-0 space-y-2">
                            <span className="text-sm font-semibold text-text-primary md:hidden">Servicio</span>
                            <input
                              type="text"
                              value={service.nombre}
                              onChange={(event) => updateService(service.id, 'nombre', event.target.value)}
                              placeholder="Corte, asesoría o reparación"
                              className={INPUT_CLASS}
                            />
                          </label>
                          <label className="min-w-0 space-y-2">
                            <span className="text-sm font-semibold text-text-primary md:hidden">Precio (opcional)</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={service.precioCop}
                              onChange={(event) => updateService(service.id, 'precioCop', event.target.value)}
                              placeholder="100500"
                              className={INPUT_CLASS}
                            />
                          </label>
                          <label className="min-w-0 space-y-2">
                            <span className="text-sm font-semibold text-text-primary md:hidden">Duración (En Minutos)</span>
                            <input
                              type="number"
                              min={15}
                              max={360}
                              step={15}
                              value={service.duracionMinutos}
                              onChange={(event) =>
                                updateService(service.id, 'duracionMinutos', Number(event.target.value))
                              }
                              className={INPUT_CLASS}
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() => removeService(service.id)}
                            disabled={services.length === 1}
                            className="flex h-11 w-full min-w-0 items-center justify-center rounded-button border border-border-strong text-text-secondary transition-colors hover:border-error hover:text-error disabled:cursor-not-allowed disabled:opacity-45 md:w-11"
                            aria-label="Eliminar servicio"
                            title="Eliminar"
                          >
                            <Trash2 size={17} />
                          </button>
                        </article>
                      ))}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={addService}
                    className="flex min-h-11 w-full items-center justify-center gap-2 rounded-button border border-dashed border-accent/65 bg-bg-primary/35 px-4 py-3 font-heading text-sm font-semibold text-accent transition-colors hover:bg-accent/10"
                  >
                    <Plus size={18} />
                    Agregar servicio
                  </button>
                </div>
              ) : null}

              {currentStepData.id === 'horarios' ? (
                <div className="min-w-0 space-y-4">
                  <div className="grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {schedules.map((schedule, index) => {
                      const isOpen = Boolean(schedule.hora_inicio && schedule.hora_fin);

                      return (
                        <article
                          key={schedule.dia_semana}
                          className={cn(
                            'min-w-0 rounded-lg border p-3 transition-colors',
                            isOpen
                              ? 'border-border bg-bg-primary/45'
                              : 'border-border-strong bg-bg-primary/25 text-text-secondary'
                          )}
                        >
                          <div className="mb-3 flex min-w-0 items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-heading text-sm font-semibold text-text-primary">
                                {DAY_LABELS[schedule.dia_semana]}
                              </p>
                              <p className="text-xs text-text-secondary">
                                {isOpen ? `${schedule.hora_inicio} - ${schedule.hora_fin}` : 'Cerrado'}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => toggleScheduleDay(index)}
                              className={cn(
                                'flex h-9 w-9 shrink-0 items-center justify-center rounded-button border transition-colors',
                                isOpen
                                  ? 'border-accent bg-accent/10 text-accent'
                                  : 'border-border-strong text-text-secondary hover:border-accent hover:text-accent'
                              )}
                              aria-label={isOpen ? 'Cerrar dia' : 'Abrir dia'}
                              title={isOpen ? 'Cerrar dia' : 'Abrir dia'}
                            >
                              {isOpen ? <Check size={16} /> : <Plus size={16} />}
                            </button>
                          </div>

                          {isOpen ? (
                            <div className="grid min-w-0 grid-cols-2 gap-2">
                              <label className="min-w-0 space-y-1">
                                <span className="text-[11px] font-semibold text-text-secondary">Inicio</span>
                                <input
                                  type="time"
                                  value={schedule.hora_inicio}
                                  onChange={(event) => updateScheduleDay(index, 'hora_inicio', event.target.value)}
                                  className="h-10 w-full min-w-0 max-w-full rounded-button border border-border-strong bg-bg-primary/70 px-2 text-xs text-text-primary outline-none transition-colors focus:border-accent"
                                />
                              </label>
                              <label className="min-w-0 space-y-1">
                                <span className="text-[11px] font-semibold text-text-secondary">Fin</span>
                                <input
                                  type="time"
                                  value={schedule.hora_fin}
                                  onChange={(event) => updateScheduleDay(index, 'hora_fin', event.target.value)}
                                  className="h-10 w-full min-w-0 max-w-full rounded-button border border-border-strong bg-bg-primary/70 px-2 text-xs text-text-primary outline-none transition-colors focus:border-accent"
                                />
                              </label>
                            </div>
                          ) : null}
                        </article>
                      );
                    })}
                  </div>

                  <div className="rounded-lg border border-border bg-bg-primary/45 p-4">
                    <div className="flex items-start gap-3">
                      <CalendarClock size={20} className="mt-0.5 shrink-0 text-accent" />
                      <div>
                        <p className="font-heading text-base font-semibold text-text-primary">{scheduleSummary}</p>
                        <p className="mt-1 text-sm leading-6 text-text-secondary">
                          Este horario se aplicara a los profesionales creados en el registro.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {currentStepData.id === 'suscripcion' ? (
                <div className="space-y-4">
                  {submitMessage ? (
                    <div
                      className={cn(
                        'flex items-start gap-3 rounded-lg border px-4 py-3 text-sm',
                        submitStatus === 'success'
                          ? 'border-success/35 bg-success/10 text-success'
                          : 'border-error/35 bg-error/10 text-error'
                      )}
                    >
                      {submitStatus === 'success' ? (
                        <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
                      ) : (
                        <AlertCircle size={18} className="mt-0.5 shrink-0" />
                      )}
                      <p>{submitMessage}</p>
                    </div>
                  ) : null}

                  <div
                    className="grid gap-3 sm:grid-cols-2"
                    role="radiogroup"
                    aria-label="Selecciona un plan"
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={selectedPlan === FREE_PLAN_CODE}
                      onClick={selectFreePlan}
                      className={cn(
                        'min-h-24 rounded-lg border p-4 text-left transition-colors',
                        selectedPlan === FREE_PLAN_CODE
                          ? 'border-accent bg-accent/10'
                          : 'border-border bg-bg-primary/40 hover:border-accent/55'
                      )}
                    >
                      <span className="flex items-center justify-between gap-3">
                        <span className="font-heading text-lg font-semibold text-text-primary">
                          Gratis
                        </span>
                        {selectedPlan === FREE_PLAN_CODE ? (
                          <CheckCircle2 size={19} className="text-accent" />
                        ) : null}
                      </span>
                      <span className="mt-2 block text-sm text-text-secondary">
                        $0 al mes · 1 profesional, 1 categoría de actividad y hasta 3 servicios
                      </span>
                    </button>

                    <button
                      type="button"
                      role="radio"
                      aria-checked={selectedPlan === MERCADO_PAGO_PLAN_CODE}
                      onClick={selectProfessionalPlan}
                      className={cn(
                        'min-h-24 rounded-lg border p-4 text-left transition-colors',
                        selectedPlan === MERCADO_PAGO_PLAN_CODE
                          ? 'border-accent bg-accent/10'
                          : 'border-border bg-bg-primary/40 hover:border-accent/55'
                      )}
                    >
                      <span className="flex items-center justify-between gap-3">
                        <span className="font-heading text-lg font-semibold text-text-primary">
                          Profesional
                        </span>
                        {selectedPlan === MERCADO_PAGO_PLAN_CODE ? (
                          <CheckCircle2 size={19} className="text-accent" />
                        ) : null}
                      </span>
                      <span className="mt-2 block text-sm text-text-secondary">
                        {formatCompactCurrency(MONTHLY_BILLING_PRICES.basePriceCop)} al mes
                        {' · '}incluye 1 profesional
                      </span>
                    </button>
                  </div>

                  <div className="grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
                    <article className="rounded-lg border border-accent/30 bg-accent/10 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-accent">
                            Plan seleccionado
                          </p>
                          <h3 className="mt-2 font-heading text-2xl font-semibold text-text-primary">
                            {selectedPlan === MERCADO_PAGO_PLAN_CODE
                              ? 'Profesional'
                              : 'Gratis'}
                          </h3>
                        </div>
                        <CreditCard size={24} className="text-accent" />
                      </div>
                      <p className="mt-4 font-heading text-3xl font-semibold text-text-primary">
                        {formatCompactCurrency(monthlyTotal)}
                        <span className="text-base font-medium text-text-secondary"> / mes</span>
                      </p>
                      <p className="mt-4 rounded-button border border-accent/25 bg-bg-primary/55 px-3 py-2 text-sm font-semibold text-text-secondary">
                        {selectedPlan === MERCADO_PAGO_PLAN_CODE
                          ? 'Bot opcional, recordatorios por WhatsApp desde la agenda, hasta 10 categorías y servicios ilimitados.'
                          : 'Agenda, clientes y reservas online sin costo. Bot de WhatsApp no incluido.'}
                      </p>
                    </article>

                    <article className="rounded-lg border border-border bg-bg-primary/45 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-text-secondary">
                        Total estimado
                      </p>
                      <dl className="mt-4 space-y-3 text-sm">
                        <div className="flex justify-between gap-3">
                          <dt className="text-text-secondary">Base mensual</dt>
                          <dd className="font-semibold text-text-primary">
                            {formatCompactCurrency(
                              selectedPlan === MERCADO_PAGO_PLAN_CODE
                                ? MONTHLY_BILLING_PRICES.basePriceCop
                                : 0
                            )}
                          </dd>
                        </div>
                        <div className="flex justify-between gap-3">
                          <dt className="text-text-secondary">
                            {extraSlots} cupo{extraSlots === 1 ? '' : 's'} extra{' '}
                            {selectedPlan === MERCADO_PAGO_PLAN_CODE ? (
                              <span className="whitespace-nowrap">
                                ({formatCompactCurrency(MONTHLY_BILLING_PRICES.extraProfessionalPriceCop)} c/u)
                              </span>
                            ) : null}
                          </dt>
                          <dd className="font-semibold text-text-primary">
                            {selectedPlan === MERCADO_PAGO_PLAN_CODE
                              ? formatCompactCurrency(
                                  extraSlots *
                                    MONTHLY_BILLING_PRICES.extraProfessionalPriceCop
                                )
                              : extraSlots > 0
                                ? 'Ocultos'
                                : formatCompactCurrency(0)}
                          </dd>
                        </div>
                        <div className="flex justify-between gap-3 border-t border-border pt-3">
                          <dt className="font-semibold text-text-primary">Mensual</dt>
                          <dd className="font-heading text-lg font-semibold text-text-primary">
                            {formatCompactCurrency(monthlyTotal)}
                          </dd>
                        </div>
                      </dl>
                    </article>
                  </div>

                  {selectedPlan === MERCADO_PAGO_PLAN_CODE ? (
                    <label className="block space-y-2 rounded-lg border border-border bg-bg-primary/40 p-4">
                      <span className="text-sm font-semibold text-text-primary">
                        Correo para el pago
                      </span>
                      <input
                        type="email"
                        value={payerEmail}
                        onChange={(event) => setPayerEmail(event.target.value)}
                        placeholder="tu@correo.com"
                        autoComplete="email"
                        className={INPUT_CLASS}
                        aria-describedby="payer-email-help"
                      />
                      <span
                        id="payer-email-help"
                        className="block text-xs leading-5 text-text-secondary"
                      >
                        Mercado Pago usará este correo para identificar el pago. Podrás revisar el total antes de confirmar.
                      </span>
                    </label>
                  ) : null}

                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                    <SummaryTile icon={Store} label="Negocio" value={businessName || 'Pendiente'} />
                    <SummaryTile
                      icon={Tags}
                      label="Actividad"
                      value={
                        primaryClassification
                          ? getBusinessTypeDisplayName(primaryClassification)
                          : 'Pendiente'
                      }
                    />
                    <SummaryTile icon={UserRound} label="Profesionales" value={String(professionals.length)} />
                    <SummaryTile icon={Scissors} label="Servicios" value={String(services.length)} />
                    <SummaryTile icon={Clock3} label="Horarios" value={scheduleSummary} />
                  </div>
                </div>
              ) : null}
            </StepShell>
          </section>

          <aside className="hidden lg:block">
            <div className="sticky top-5 overflow-hidden rounded-card border border-border bg-bg-secondary/92 p-5 shadow-mockup">
              <div className="relative h-44 overflow-hidden rounded-lg border border-border bg-bg-primary">
                <Image
                  src="/images/directory-hero.webp"
                  alt="Vista previa del perfil del negocio"
                  fill
                  sizes="360px"
                  className="object-cover"
                  priority
                />
                <div className="absolute inset-0" />
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="font-heading text-lg font-semibold  text-bg-primary">
                    {businessName || 'Tu negocio'}
                  </p>
                  <p className="text-sm  text-bg-primary">
                    {businessPhone ? formatPhoneForDisplay(businessPhone) : 'Telefono pendiente'}
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <h2 className="font-heading text-xl font-semibold text-text-primary">Resumen</h2>
                <div className="mt-4 divide-y divide-border">
                  <PreviewRow
                    icon={Store}
                    label="Negocio"
                    value={businessName ? 'Completo' : 'Sin datos'}
                  />
                  <PreviewRow
                    icon={Tags}
                    label="Actividad"
                    value={
                      primaryClassification
                        ? getBusinessTypeDisplayName(primaryClassification)
                        : 'Sin seleccionar'
                    }
                  />
                  <PreviewRow icon={UserRound} label="Profesionales" value={String(professionals.length)} />
                  <PreviewRow icon={Scissors} label="Servicios" value={String(services.length)} />
                  <PreviewRow icon={Clock3} label="Horarios" value={scheduleSummary} />
                  <PreviewRow
                    icon={CreditCard}
                    label="Plan"
                    value={
                      selectedPlan === MERCADO_PAGO_PLAN_CODE
                        ? 'Profesional'
                        : 'Gratis'
                    }
                  />
                </div>
              </div>

              <div className="mt-6 rounded-lg border border-border bg-bg-primary/50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-text-secondary">Total mensual</span>
                  <span className="font-heading text-xl font-semibold text-text-primary">
                    {formatCompactCurrency(monthlyTotal)}
                  </span>
                </div>
                <p className="mt-2 text-xs leading-5 text-text-secondary">
                  {selectedPlan === MERCADO_PAGO_PLAN_CODE && extraSlots > 0
                    ? `${extraSlots} cupo${extraSlots === 1 ? '' : 's'} extra × ${formatCompactCurrency(MONTHLY_BILLING_PRICES.extraProfessionalPriceCop)}.`
                    : selectedPlan === FREE_PLAN_CODE && extraSlots > 0
                      ? `${extraSlots} profesional${extraSlots === 1 ? '' : 'es'} adicional${extraSlots === 1 ? '' : 'es'} quedará${extraSlots === 1 ? '' : 'n'} guardado${extraSlots === 1 ? '' : 's'} y oculto${extraSlots === 1 ? '' : 's'}.`
                      : 'Incluye 1 profesional.'}
                </p>
              </div>

              <div className="mt-5 flex items-center gap-2 text-xs text-text-secondary">
                <ShieldCheck size={15} className="text-accent" />
                <span>Tu informacion esta segura con nosotros</span>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-bg-secondary/96 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 shadow-[0_-18px_50px_rgb(34_31_28/0.16)] backdrop-blur-xl lg:hidden">
        <div className="mx-auto grid w-full min-w-0 max-w-2xl grid-cols-2 gap-3">
          <Button type="button" variant="secondary" onClick={goBack} disabled={currentStep === 0 || isSubmitting}>
            <ArrowLeft size={18} />
            Atras
          </Button>
          {isSuccess ? (
            <Button asChild>
              <Link href="/login/">
                Ir al login
                <ArrowRight size={18} />
              </Link>
            </Button>
          ) : (
            <Button
              type="button"
              onClick={isFinalStep ? handleSubmit : goNext}
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? <LoaderCircle size={18} className="animate-spin" /> : null}
              {isFinalStep
                ? selectedPlan === MERCADO_PAGO_PLAN_CODE
                  ? 'Crear y pagar'
                  : 'Crear negocio'
                : 'Siguiente'}
            </Button>
          )}
        </div>
      </div>

      <ProfessionalUpsellDialog
        open={upsellContext !== null}
        context={upsellContext ?? 'additional-category'}
        source="registration"
        onClose={() => setUpsellContext(null)}
        onUpgrade={selectProfessionalPlan}
      />
    </main>
  );
}

function PreviewRow({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Store;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-4">
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-button border border-accent/25 bg-accent/10 text-accent">
          <Icon size={17} />
        </span>
        <span className="truncate text-sm text-text-secondary">{label}</span>
      </div>
      <span className="max-w-[150px] truncate text-right text-sm font-semibold text-text-primary">{value}</span>
    </div>
  );
}

function SummaryTile({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Store;
  label: string;
  value: string;
}) {
  return (
    <article className="rounded-lg border border-border bg-bg-primary/45 p-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-text-secondary">
        <Icon size={15} className="text-accent" />
        {label}
      </div>
      <p className="mt-3 truncate font-heading text-base font-semibold text-text-primary">{value}</p>
    </article>
  );
}
